import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { userActions } from '../../core/action/userAction';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import dayjs from 'dayjs';
import Select from 'react-select';
import { DatePicker } from 'antd';

// Icons
import {
    ChevronUp,
    PlusCircle,
    RotateCcw,
    Eye,
    Edit,
    Filter,
    Search,
    Trash2
} from 'feather-icons-react/build/IconComponents';

// Actions
import { purchaseReturnActions } from '../../core/action/purchaseReturnActions';
import { purchaseActions } from '../../core/action/purchaseActions';

// Modals
import AddPurchaseReturn from '../../core/modals/purchases/addpurchasereturn';
import EditPurchaseReturn from '../../core/modals/purchases/editpurchasereturns';
import ViewPurchaseReturn from '../../core/modals/purchases/viewpurchasereturn';

const PurchaseReturns = () => {
    const dispatch = useDispatch();
    const MySwal = withReactContent(Swal);
    const data = useSelector((state) => state.toggle_header);

    // Get user role for super admin check
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.role_slug === 'super-admin';

    // Permission checks
    const canCreate = userActions.hasPermission('create-purchase-returns');
    const canEdit = userActions.hasPermission('edit-purchase-returns');
    const canDelete = userActions.hasPermission('delete-purchase-returns');
    const canView = userActions.hasPermission('view-purchase-returns');

    // State for purchase returns data
    const [purchaseReturns, setPurchaseReturns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isFilterVisible, setIsFilterVisible] = useState(false);

    // Dropdown options
    const [suppliers, setSuppliers] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [paymentStatusOptions, setPaymentStatusOptions] = useState([]);

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Filter states
    const [filters, setFilters] = useState({
        search: '',
        startDate: null,
        endDate: null,
        supplier: '',
        status: '',
        paymentStatus: ''
    });

    // Sort state
    const [sortOption, setSortOption] = useState('newest');

    // Sort options
    const sortOptions = [
        { value: 'newest', label: 'Newest First' },
        { value: 'oldest', label: 'Oldest First' },
        { value: 'amount_high', label: 'Highest Amount' },
        { value: 'amount_low', label: 'Lowest Amount' },
        { value: 'date_asc', label: 'Date Ascending' },
        { value: 'date_desc', label: 'Date Descending' }
    ];

    // Status options for filter
    const statusFilterOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'received', label: 'Received' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Payment status options for filter
    const paymentStatusFilterOptions = [
        { value: '', label: 'All Payment Status' },
        { value: 'paid', label: 'Paid' },
        { value: 'partial', label: 'Partial' },
        { value: 'unpaid', label: 'Unpaid' }
    ];

    // Load purchase returns with filters
    const loadPurchaseReturns = useCallback(async (page = pagination.page) => {
        // Check if user has view permission
        if (!canView && !isSuperAdmin) {
            console.warn('User does not have permission to view purchase returns');
            return;
        }

        try {
            setLoading(true);

            const params = {
                page: page,
                limit: pagination.limit,
                search: filters.search,
            };

            // Map sort option to API sort params
            const sortMap = {
                'newest': { sort_by: 'created_at', sort_order: 'DESC' },
                'oldest': { sort_by: 'created_at', sort_order: 'ASC' },
                'amount_high': { sort_by: 'grand_total', sort_order: 'DESC' },
                'amount_low': { sort_by: 'grand_total', sort_order: 'ASC' },
                'date_asc': { sort_by: 'return_date', sort_order: 'ASC' },
                'date_desc': { sort_by: 'return_date', sort_order: 'DESC' }
            };
            const sortParams = sortMap[sortOption] || { sort_by: 'created_at', sort_order: 'DESC' };
            params.sort_by = sortParams.sort_by;
            params.sort_order = sortParams.sort_order;

            if (filters.startDate) {
                params.start_date = dayjs(filters.startDate).format('YYYY-MM-DD');
            }
            if (filters.endDate) {
                params.end_date = dayjs(filters.endDate).format('YYYY-MM-DD');
            }
            if (filters.supplier) {
                params.supplier_id = filters.supplier;
            }
            if (filters.status) {
                params.status = filters.status;
            }
            if (filters.paymentStatus) {
                params.payment_status = filters.paymentStatus;
            }

            const result = await purchaseReturnActions.getReturns(params);

            if (result.success) {
                setPurchaseReturns(result.data || []);

                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load purchase returns',
                    timer: 2000
                });
            }
        } catch (error) {
            console.error('Error loading purchase returns:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load purchase returns',
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    }, [canView, isSuperAdmin, pagination.page, pagination.limit, filters, sortOption, MySwal]);

    // Load suppliers for dropdown
    const loadInitialData = useCallback(async () => {
        try {
            const suppliersResult = await purchaseActions.getSuppliers();
            if (suppliersResult.success) {
                const formattedSuppliers = suppliersResult.data.map(supplier => ({
                    value: supplier.id,
                    label: supplier.name || supplier.supplier_name,
                    phone: supplier.phone
                }));
                setSuppliers([{ value: '', label: 'All Suppliers' }, ...formattedSuppliers]);
            }

            setStatusOptions(statusFilterOptions);
            setPaymentStatusOptions(paymentStatusFilterOptions);
        } catch (error) {
            console.error('Error loading initial data:', error);
        }
    }, []);

    // Handle add click
    const handleAddClick = () => {
        if (!canCreate && !isSuperAdmin) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to create purchase returns',
                icon: 'error',
                timer: 3000
            });
            return;
        }
        setShowAddModal(true);
    };

    // Handle edit click
    const handleEditClick = (returnItem) => {
        if (!canEdit && !isSuperAdmin) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to edit purchase returns',
                icon: 'error',
                timer: 3000
            });
            return;
        }
        setSelectedReturn(returnItem);
        setShowEditModal(true);
    };

    // Handle view click
    const handleViewClick = (returnItem) => {
        if (!canView && !isSuperAdmin) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to view purchase return details',
                icon: 'error',
                timer: 3000
            });
            return;
        }
        setSelectedReturn(returnItem);
        setShowViewModal(true);
    };

    // Handle delete single return
    const handleDeleteReturn = async (id, referenceNo) => {
        if (!canDelete && !isSuperAdmin) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to delete purchase returns',
                icon: 'error',
                timer: 3000
            });
            return;
        }

        const result = await MySwal.fire({
            title: 'Delete Purchase Return?',
            html: `<p>Are you sure you want to delete return <strong>${referenceNo}</strong>?</p>
                   <p class="text-danger">This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const deleteResult = await purchaseReturnActions.deleteReturn(id);

                if (deleteResult.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Purchase return has been deleted.',
                        timer: 2000
                    });
                    loadPurchaseReturns(pagination.page);
                } else {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: deleteResult.message || 'Failed to delete purchase return',
                        timer: 2000
                    });
                }
            } catch (error) {
                console.error('Error deleting purchase return:', error);
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to delete purchase return',
                    timer: 2000
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle modal close
    const handleEditModalClose = () => {
        setShowEditModal(false);
        setSelectedReturn(null);
    };

    const handleViewModalClose = () => {
        setShowViewModal(false);
        setSelectedReturn(null);
    };

    // Handle success callback
    const handleSuccess = () => {
        loadPurchaseReturns(pagination.page);
        handleEditModalClose();
        handleViewModalClose();
        setShowAddModal(false);
    };

    // Handle search with debounce
    const handleSearch = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, search: value }));

        const timeoutId = setTimeout(() => {
            setPagination(prev => ({ ...prev, page: 1 }));
            loadPurchaseReturns(1);
        }, 500);

        return () => clearTimeout(timeoutId);
    };

    // Handle filter changes
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    };

    // Apply filters
    const handleApplyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadPurchaseReturns(1);
        setIsFilterVisible(false);
    };

    // Reset filters
    const handleResetFilters = () => {
        setFilters({
            search: '',
            startDate: null,
            endDate: null,
            supplier: '',
            status: '',
            paymentStatus: ''
        });
        setSortOption('newest');
        setPagination(prev => ({ ...prev, page: 1 }));
        loadPurchaseReturns(1);
        setIsFilterVisible(false);
    };

    // Handle sort change
    const handleSortChange = (selectedOption) => {
        setSortOption(selectedOption.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
            loadPurchaseReturns(newPage);
        }
    };

    // Row selection handlers
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedRows(purchaseReturns.map(item => item.id));
        } else {
            setSelectedRows([]);
        }
    };

    const handleSelectRow = (id) => {
        setSelectedRows(prev =>
            prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
        );
    };

    // Bulk delete
    const handleBulkDelete = async () => {
        if (!canDelete && !isSuperAdmin) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to delete purchase returns',
                icon: 'error',
                timer: 3000
            });
            return;
        }

        if (selectedRows.length === 0) {
            MySwal.fire({
                icon: 'info',
                title: 'No Selection',
                text: 'Please select at least one purchase return to delete.',
                timer: 2000
            });
            return;
        }

        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: `You want to delete ${selectedRows.length} purchase return(s)? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: `Yes, delete ${selectedRows.length} item(s)`,
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);

                const deletePromises = selectedRows.map(id =>
                    purchaseReturnActions.deleteReturn(id)
                );

                const results = await Promise.allSettled(deletePromises);
                const successfulDeletes = results.filter(r => r.status === 'fulfilled' && r.value.success);

                if (successfulDeletes.length > 0) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: `${successfulDeletes.length} purchase return(s) have been deleted.`,
                        timer: 2000
                    });

                    setSelectedRows([]);

                    // Refresh if current page becomes empty
                    if (purchaseReturns.length === selectedRows.length && pagination.page > 1) {
                        handlePageChange(pagination.page - 1);
                    } else {
                        loadPurchaseReturns(pagination.page);
                    }
                } else {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: 'Failed to delete purchase returns',
                        timer: 2000
                    });
                }
            } catch (error) {
                console.error('Error in bulk delete:', error);
                MySwal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'Failed to delete purchase returns',
                    timer: 2000
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Refresh
    const handleRefresh = () => {
        loadPurchaseReturns(pagination.page);
    };

    // Toggle filter
    const toggleFilterVisibility = () => {
        setIsFilterVisible(prev => !prev);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('DD/MM/YYYY');
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'pending':
                return <span className="badge bg-warning">Pending</span>;
            case 'approved':
                return <span className="badge bg-info">Approved</span>;
            case 'received':
                return <span className="badge bg-success">Received</span>;
            case 'rejected':
                return <span className="badge bg-danger">Rejected</span>;
            case 'completed':
                return <span className="badge bg-success">Completed</span>;
            case 'cancelled':
                return <span className="badge bg-secondary">Cancelled</span>;
            default:
                return <span className="badge bg-secondary">{status || 'Unknown'}</span>;
        }
    };

    // Get payment status badge
    const getPaymentStatusBadge = (status) => {
        const statusMap = {
            'paid': 'bg-success',
            'partial': 'bg-warning',
            'unpaid': 'bg-danger'
        };
        return <span className={`badge ${statusMap[status?.toLowerCase()] || 'bg-secondary'}`}>
            {status || 'Unknown'}
        </span>;
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Calculate summary statistics
    const getSummaryStats = () => {
        const stats = {
            totalReturns: purchaseReturns.length,
            totalAmount: 0,
            totalPaid: 0,
            totalDue: 0,
            pendingCount: 0,
            completedCount: 0
        };

        purchaseReturns.forEach(item => {
            stats.totalAmount += item.grand_total || 0;
            stats.totalPaid += item.paid_amount || 0;
            stats.totalDue += item.due_amount || 0;

            if (item.status === 'pending' || item.status === 'approved') {
                stats.pendingCount++;
            } else if (item.status === 'completed' || item.status === 'received') {
                stats.completedCount++;
            }
        });

        return stats;
    };

    const stats = getSummaryStats();

    // Initialize component
    useEffect(() => {
        if (canView || isSuperAdmin) {
            loadInitialData();
            loadPurchaseReturns();
        }
    }, []); // Empty dependency array

    // Update when sort, page, or limit changes
    useEffect(() => {
        if (canView || isSuperAdmin) {
            loadPurchaseReturns();
        }
    }, [sortOption, pagination.page, pagination.limit]);

    // Tooltip renderers
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Purchase Return Management</h4>
                        <h6>Manage your purchase returns and supplier credits</h6>
                    </div>

                    <ul className="table-top-head">

                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={handleRefresh}>
                                    <RotateCcw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        {/* Bulk Delete Button - Only show if user has delete permission */}
                        {(canDelete || isSuperAdmin) && selectedRows.length > 0 && (
                            <li>
                                <button
                                    className="btn btn-danger btn-sm"
                                    onClick={handleBulkDelete}
                                    disabled={loading}
                                >
                                    <Trash2 size={16} className="me-1" />
                                    Delete ({selectedRows.length})
                                </button>
                            </li>
                        )}
                    </ul>

                    {/* Add Purchase Return Button - Only show if user has create permission */}
                    <div className="page-btn">
                        {(canCreate || isSuperAdmin) && (
                            <button
                                className="btn btn-primary"
                                onClick={handleAddClick}
                            >
                                <PlusCircle size={18} className="me-2" />
                                Add Purchase Return
                            </button>
                        )}
                    </div>
                </div>

                {/* Summary Cards - Only show if user has view permission */}
                {(canView || isSuperAdmin) && (
                    <div className="row mb-4">
                        <div className="col-xl-2 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <h6 className="card-title text-muted">Total Returns</h6>
                                    <h3 className="mb-0">{pagination.total}</h3>
                                    <small className="text-muted">All time</small>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-2 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <h6 className="card-title text-muted">Return Amount</h6>
                                    <h3 className="mb-0 text-primary">{formatCurrency(stats.totalAmount)}</h3>
                                    <small className="text-muted">Total value</small>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-2 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <h6 className="card-title text-muted">Paid Amount</h6>
                                    <h3 className="mb-0 text-success">{formatCurrency(stats.totalPaid)}</h3>
                                    <small className="text-muted">Amount received</small>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-2 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <h6 className="card-title text-muted">Due Amount</h6>
                                    <h3 className="mb-0 text-danger">{formatCurrency(stats.totalDue)}</h3>
                                    <small className="text-muted">Pending payment</small>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-2 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <h6 className="card-title text-muted">Pending</h6>
                                    <h3 className="mb-0 text-warning">{stats.pendingCount}</h3>
                                    <small className="text-muted">Awaiting action</small>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-2 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <h6 className="card-title text-muted">Completed</h6>
                                    <h3 className="mb-0 text-success">{stats.completedCount}</h3>
                                    <small className="text-muted">Processed</small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Card */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : !canView && !isSuperAdmin ? (
                            <div className="text-center py-5">
                                <i data-feather="lock" className="feather-lock" style={{ fontSize: '48px', opacity: 0.5 }} />
                                <h5 className="mt-3">Access Denied</h5>
                                <p className="text-muted">You dont have permission to view purchase returns</p>
                            </div>
                        ) : (
                            <>
                                {/* Search and Filter Bar - Only show if user has view permission */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex">
                                        <div className="search-set me-3">
                                            <div className="search-input">
                                                <input
                                                    type="text"
                                                    placeholder="Search by reference, supplier..."
                                                    className="form-control"
                                                    value={filters.search}
                                                    onChange={handleSearch}
                                                />
                                                <button className="btn btn-searchset" onClick={() => loadPurchaseReturns(1)}>
                                                    <Search size={16} />
                                                </button>
                                            </div>
                                        </div>
                                        <button
                                            className={`btn btn-filter me-2 ${isFilterVisible ? 'active' : ''}`}
                                            onClick={toggleFilterVisibility}
                                        >
                                            <Filter size={16} className="me-1" />
                                            Filters
                                        </button>
                                    </div>
                                    <div className="d-flex">
                                        <div className="form-sort" style={{ minWidth: '200px' }}>
                                            <Select
                                                options={sortOptions}
                                                value={sortOptions.find(opt => opt.value === sortOption)}
                                                onChange={handleSortChange}
                                                isClearable={false}
                                                placeholder="Sort by"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Filter Panel */}
                                {isFilterVisible && (
                                    <div className="card mb-3 border">
                                        <div className="card-body">
                                            <div className="row g-3">
                                                <div className="col-lg-3">
                                                    <label className="form-label">From Date</label>
                                                    <DatePicker
                                                        className="form-control"
                                                        value={filters.startDate}
                                                        onChange={(date) => handleFilterChange('startDate', date)}
                                                        format="DD-MM-YYYY"
                                                        placeholder="From Date"
                                                        style={{ width: '100%' }}
                                                    />
                                                </div>
                                                <div className="col-lg-3">
                                                    <label className="form-label">To Date</label>
                                                    <DatePicker
                                                        className="form-control"
                                                        value={filters.endDate}
                                                        onChange={(date) => handleFilterChange('endDate', date)}
                                                        format="DD-MM-YYYY"
                                                        placeholder="To Date"
                                                        style={{ width: '100%' }}
                                                    />
                                                </div>
                                                <div className="col-lg-2">
                                                    <label className="form-label">Supplier</label>
                                                    <Select
                                                        options={suppliers}
                                                        value={suppliers.find(s => s.value === filters.supplier)}
                                                        onChange={(opt) => handleFilterChange('supplier', opt?.value || '')}
                                                        placeholder="All Suppliers"
                                                        isClearable
                                                    />
                                                </div>
                                                <div className="col-lg-2">
                                                    <label className="form-label">Status</label>
                                                    <Select
                                                        options={statusOptions}
                                                        value={statusOptions.find(s => s.value === filters.status)}
                                                        onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                                                        placeholder="All Status"
                                                        isClearable
                                                    />
                                                </div>
                                                <div className="col-lg-2">
                                                    <label className="form-label">Payment Status</label>
                                                    <Select
                                                        options={paymentStatusOptions}
                                                        value={paymentStatusOptions.find(s => s.value === filters.paymentStatus)}
                                                        onChange={(opt) => handleFilterChange('paymentStatus', opt?.value || '')}
                                                        placeholder="All Payment"
                                                        isClearable
                                                    />
                                                </div>
                                                <div className="col-12">
                                                    <button className="btn btn-primary me-2" onClick={handleApplyFilters}>
                                                        Apply Filters
                                                    </button>
                                                    <button className="btn btn-secondary" onClick={handleResetFilters}>
                                                        Reset
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Table */}
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                {/* Checkbox column - Only show if user has delete permission */}
                                                {(canDelete || isSuperAdmin) && (
                                                    <th style={{ width: '40px' }}>
                                                        <div className="form-check">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selectedRows.length === purchaseReturns.length && purchaseReturns.length > 0}
                                                                onChange={handleSelectAll}
                                                            />
                                                        </div>
                                                    </th>
                                                )}
                                                <th>Date</th>
                                                <th>Return Ref</th>
                                                <th>Original Purchase</th>
                                                <th>Supplier</th>
                                                <th>Status</th>
                                                <th className="text-end">Grand Total</th>
                                                <th className="text-end">Paid</th>
                                                <th className="text-end">Due</th>
                                                <th>Payment Status</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {purchaseReturns.length === 0 ? (
                                                <tr>
                                                    <td colSpan="12" className="text-center py-5">
                                                        <div className="text-muted">
                                                            <h6>No purchase returns found</h6>
                                                            <p>Try adjusting your search or filters</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                purchaseReturns.map((item) => (
                                                    <tr key={item.id}>
                                                        {/* Checkbox cell - Only show if user has delete permission */}
                                                        {(canDelete || isSuperAdmin) && (
                                                            <td>
                                                                <div className="form-check">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input"
                                                                        checked={selectedRows.includes(item.id)}
                                                                        onChange={() => handleSelectRow(item.id)}
                                                                    />
                                                                </div>
                                                            </td>
                                                        )}
                                                        <td>{formatDate(item.return_date || item.created_at)}</td>
                                                        <td>
                                                            <strong>{item.reference_no}</strong>
                                                        </td>
                                                        <td>
                                                            {item.original_reference_no || item.purchase_reference || 'N/A'}
                                                        </td>
                                                        <td>{item.supplier_name || item.supplier}</td>
                                                        <td>{getStatusBadge(item.status)}</td>
                                                        <td className="text-end fw-bold">{formatCurrency(item.grand_total)}</td>
                                                        <td className="text-end text-success">{formatCurrency(item.paid_amount)}</td>
                                                        <td className={`text-end fw-bold ${item.due_amount > 0 ? 'text-danger' : 'text-success'}`}>
                                                            {formatCurrency(item.due_amount)}
                                                        </td>
                                                        <td>{getPaymentStatusBadge(item.payment_status)}</td>
                                                        <td className="text-center">
                                                            {/* View Button - Show if user has view permission */}
                                                            {(canView || isSuperAdmin) && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-info me-1"
                                                                    onClick={() => handleViewClick(item)}
                                                                    title="View Details"
                                                                >
                                                                    <Eye size={14} />
                                                                </button>
                                                            )}

                                                            {/* Edit Button - Show if user has edit permission */}
                                                            {(canEdit || isSuperAdmin) && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary me-1"
                                                                    onClick={() => handleEditClick(item)}
                                                                    title="Edit Return"
                                                                >
                                                                    <Edit size={14} />
                                                                </button>
                                                            )}

                                                            {/* Delete Button - Show if user has delete permission */}
                                                            {(canDelete || isSuperAdmin) && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleDeleteReturn(item.id, item.reference_no)}
                                                                    title="Delete Return"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination.totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                                            {pagination.total} entries
                                        </div>
                                        <nav>
                                            <ul className="pagination">
                                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pagination.page - 1)}
                                                    >
                                                        Previous
                                                    </button>
                                                </li>

                                                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                                    let pageNum = pagination.page;
                                                    if (pagination.totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (pagination.page <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (pagination.page >= pagination.totalPages - 2) {
                                                        pageNum = pagination.totalPages - 4 + i;
                                                    } else {
                                                        pageNum = pagination.page - 2 + i;
                                                    }

                                                    return (
                                                        <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => handlePageChange(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        </li>
                                                    );
                                                })}

                                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pagination.page + 1)}
                                                    >
                                                        Next
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            {showAddModal && (
                <AddPurchaseReturn
                    onSuccess={handleSuccess}
                    onClose={() => setShowAddModal(false)}
                    isOpen={showAddModal}
                />
            )}

            {showEditModal && selectedReturn && (
                <EditPurchaseReturn
                    returnId={selectedReturn.id}
                    isViewMode={false}
                    onSuccess={handleSuccess}
                    onClose={handleEditModalClose}
                />
            )}

            {showViewModal && selectedReturn && (
                <ViewPurchaseReturn
                    returnData={selectedReturn}
                    onClose={handleViewModalClose}
                />
            )}
        </div>
    );
};

export default PurchaseReturns;